import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from './Header';
import TabNavigation from './TabNavigation';
import Navbar from '../landing/Navbar';
import { validateSession } from '../../utils/api';

interface AppLayoutProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  showTabs?: boolean;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  requireAuth = true,
  showTabs = true,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('idToken');
      
      if (!token) {
        // No token - user is not authenticated
        if (requireAuth) {
          // Redirect to login for protected routes
          const redirectPath = location.pathname + location.search;
          navigate('/login?redirect=' + encodeURIComponent(redirectPath));
          return;
        }
        // Public route - allow access without auth
        setAuthenticated(false);
        setChecking(false);
        return;
      }

      // Has token - validate session
      const { valid } = await validateSession();
      if (valid) {
        setAuthenticated(true);
      // Redirect to /home once per calendar day for authenticated users.
      // Uses localStorage with today's date so it fires on any route,
      // not just when the user hits / (fixes #343).
      const today = new Date().toISOString().slice(0, 10); // 'YYYY-MM-DD'
      const seenHomeDate = localStorage.getItem('percoro_seen_home_date');
      const currentPath = window.location.pathname;
      // Never redirect if already on /home or auth flow pages
      const neverRedirect = ['/home', '/login', '/signup', '/confirm-signup', '/forgot-password', '/reset-password'];
      if (seenHomeDate !== today && !neverRedirect.some(p => currentPath.startsWith(p))) {
        localStorage.setItem('percoro_seen_home_date', today);
        navigate('/home', { replace: true });
        return;
      }
      if (currentPath === '/home') {
        localStorage.setItem('percoro_seen_home_date', today);
      }
      } else {
        // Session invalid
        if (requireAuth) {
          const redirectPath = location.pathname + location.search;
          sessionStorage.setItem('authMessage', 'Session expired. Please log in again.');
          navigate('/login?redirect=' + encodeURIComponent(redirectPath));
          return;
        }
        // Public route - allow access even with invalid token
        setAuthenticated(false);
      }
      setChecking(false);
    };

    checkAuth();
  }, [requireAuth, navigate, location]);

  if (checking) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // For authenticated users: show Header + TabNavigation
  if (authenticated) {
    const headerHeight = 64;
    const tabsHeight = showTabs ? 56 : 0;
    const topPadding = headerHeight + tabsHeight;

    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
        <Header />
        {showTabs && <TabNavigation />}
        <Box
          component="main"
          sx={{
            pt: topPadding + 'px',
            minHeight: '100vh',
          }}
        >
          {children}
        </Box>
      </Box>
    );
  }

  // For unauthenticated users on public pages: show landing Navbar
  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <Navbar />
      <Box
        component="main"
        sx={{
          pt: '64px', // Navbar height
          minHeight: '100vh',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default AppLayout;
